import { api } from '@goaltree/backend/convex/_generated/api'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation, useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import { ConvexError } from 'convex/values'
import { Globe } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { UserAvatar } from '@/components/user-avatar'

type Item = FunctionReturnType<typeof api.live.stream>['items'][number]
type FriendItem = Extract<Item, { layer: 'friend' }>
type BoostKind = 'water' | 'sun' | 'fire' | 'summit'

const COLLAPSED = 5
const EXPANDED = 20
const TICK_MS = 15_000
const EMOJI: Record<BoostKind, string> = { water: '💧', sun: '☀️', fire: '🔥', summit: '🏔️' }

function defaultBoost(kind: Item['kind']): BoostKind {
  if (kind === 'milestone' || kind === 'goal') return 'summit'
  if (kind === 'streak') return 'fire'
  if (kind === 'badge') return 'sun'
  return 'water'
}

function liveAgo(ms: number, now: number, locale: string) {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'narrow' })
  const secs = Math.max(1, Math.round((now - ms) / 1000))
  if (secs < 60) return rtf.format(-secs, 'second')
  const mins = Math.round(secs / 60)
  if (mins < 60) return rtf.format(-mins, 'minute')
  const hours = Math.round(mins / 60)
  if (hours < 24) return rtf.format(-hours, 'hour')
  return rtf.format(-Math.round(hours / 24), 'day')
}

function useNow() {
  const [now, setNow] = useState(Date.now)
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS)
    return () => clearInterval(id)
  }, [])
  return now
}

function useLiveBoost() {
  const { t } = useTranslation()
  const mutate = useMutation(api.activity.boost).withOptimisticUpdate(
    (store, { eventId, kind }) => {
      const live = store.getQuery(api.live.stream, {})
      if (live)
        store.setQuery(
          api.live.stream,
          {},
          {
            items: live.items.map((it) => {
              if (it.layer !== 'friend' || it.id !== eventId) return it
              const next = it.myBoost === kind ? null : kind
              return {
                ...it,
                myBoost: next,
                boostCount: it.boostCount + (next ? 1 : 0) - (it.myBoost ? 1 : 0),
              }
            }),
          },
        )
      for (const { args, value } of store.getAllQueries(api.activity.getFeed)) {
        if (!value?.cards.some((c) => c.id === eventId)) continue
        store.setQuery(api.activity.getFeed, args, {
          ...value,
          cards: value.cards.map((c) => {
            if (c.id !== eventId) return c
            const counts = { ...c.counts }
            const next = c.myBoost === kind ? null : kind
            if (c.myBoost) counts[c.myBoost]--
            if (next) counts[next]++
            return {
              ...c,
              counts,
              myBoost: next,
              boostCount: c.boostCount + (next ? 1 : 0) - (c.myBoost ? 1 : 0),
            }
          }),
        })
      }
    },
  )
  return (item: FriendItem) =>
    mutate({ eventId: item.id, kind: item.myBoost ?? defaultBoost(item.kind) }).catch((err) => {
      if (!(err instanceof ConvexError)) toast.error(t('common.error'))
    })
}

function LiveLine({ item }: { item: Item }) {
  const { t } = useTranslation()
  const who =
    item.layer === 'friend'
      ? item.name
      : item.city
        ? t('live.someoneIn', { city: item.city })
        : t('live.someone')
  const key =
    item.kind === 'streak' || item.title !== null ? item.kind : (`${item.kind}Anon` as const)
  return (
    <Trans
      i18nKey={`live.event.${key}`}
      values={{ who, title: item.title ?? '', days: item.streak ?? 0, level: item.level ?? 0 }}
      components={{
        n: (
          <span
            className={cn(
              'font-semibold',
              item.layer === 'friend' ? 'text-foreground' : 'text-foreground/70',
            )}
          />
        ),
        b: <span className="font-medium text-foreground italic" />,
      }}
    />
  )
}

export function LiveStrip() {
  const { t, i18n } = useTranslation()
  const data = useQuery(api.live.stream)
  const [expanded, setExpanded] = useState(false)
  const now = useNow()
  const reduce = useReducedMotion()
  const boost = useLiveBoost()
  if (!data?.items.length) return null
  const shown = data.items.slice(0, expanded ? EXPANDED : COLLAPSED)

  return (
    <section aria-labelledby="live-heading" className="mb-6">
      <h2
        id="live-heading"
        className="mb-2 flex items-center gap-2 text-xs font-semibold tracking-widest text-muted-foreground uppercase"
      >
        <span className="relative flex size-2">
          <span className="absolute inline-flex size-full animate-ping rounded-full bg-primary opacity-60 motion-reduce:animate-none" />
          <span className="relative inline-flex size-2 rounded-full bg-primary" />
        </span>
        {t('live.title')}
      </h2>
      <ul className="overflow-hidden rounded-2xl border border-border/60 bg-card">
        <AnimatePresence initial={false}>
          {shown.map((item) => (
            <motion.li
              key={item.layer === 'friend' ? item.id : item.key}
              layout={reduce ? false : 'position'}
              initial={reduce ? false : { opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? undefined : { opacity: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="flex items-center gap-2.5 border-b border-border/40 px-3 py-2 last:border-b-0"
            >
              {item.layer === 'friend' ? (
                <UserAvatar
                  image={item.image}
                  name={item.name}
                  size={28}
                  className="size-7 shrink-0 rounded-full bg-muted object-cover"
                />
              ) : (
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted/60">
                  <Globe className="size-3.5 text-muted-foreground" />
                </span>
              )}
              <p
                className={cn(
                  'min-w-0 flex-1 text-[13px] leading-snug',
                  item.layer === 'friend' ? 'text-muted-foreground' : 'text-muted-foreground/80',
                )}
              >
                <LiveLine item={item} />
                <span className="ml-1.5 font-mono text-[11px] text-muted-foreground/70">
                  {liveAgo(item.createdAt, now, i18n.language)}
                </span>
              </p>
              {item.layer === 'friend' && (
                <button
                  type="button"
                  aria-pressed={!!item.myBoost}
                  aria-label={t('live.boost', { name: item.name })}
                  title={t('live.boost', { name: item.name })}
                  onClick={() => boost(item)}
                  className={cn(
                    'flex h-7 shrink-0 cursor-pointer items-center gap-1 rounded-full border px-2 text-xs transition-colors',
                    item.myBoost
                      ? 'border-primary/40 bg-primary/10 text-primary-text'
                      : 'border-border/60 text-muted-foreground hover:bg-muted/40 hover:text-foreground',
                  )}
                >
                  <span className="text-[13px] leading-none">
                    {EMOJI[item.myBoost ?? defaultBoost(item.kind)]}
                  </span>
                  {item.boostCount > 0 && (
                    <span className="font-mono text-[11px] font-semibold">{item.boostCount}</span>
                  )}
                </button>
              )}
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>
      {data.items.length > COLLAPSED && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="mx-auto mt-2 block cursor-pointer rounded-full px-3 py-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
        >
          {expanded ? t('live.showLess') : t('live.showMore')}
        </button>
      )}
    </section>
  )
}
